import AsyncStorage from '@react-native-async-storage/async-storage';
import {SafeAuthError} from '../auth/authErrorPolicy';
import {track} from './analytics';

const BAY_PLAYER_JWT_STORAGE_KEY = 'bay_player_jwt';

export const BAY_PLAYER_JWT_WRITE_ERROR_MESSAGE = 'We could not save your session on this device. Please try again.';

export const BAY_PLAYER_JWT_READ_ERROR_MESSAGE = 'We could not restore your session from this device. Please try again.';

const BAY_PLAYER_JWT_REMOVE_ERROR_MESSAGE = 'We could not clear the previous session on this device. Please restart the app and try again.';

type BayPlayerCredentialStorageOperation = 'read' | 'write' | 'remove';

class BayPlayerCredentialStorageError extends SafeAuthError {
  readonly operation: BayPlayerCredentialStorageOperation;

  constructor(operation: BayPlayerCredentialStorageOperation) {
    super({
      category: 'device_storage', code: `credential_storage_${operation}`, retryable: operation !== 'remove',
      userMessage: operation === 'read' ? BAY_PLAYER_JWT_READ_ERROR_MESSAGE : operation === 'write' ? BAY_PLAYER_JWT_WRITE_ERROR_MESSAGE : BAY_PLAYER_JWT_REMOVE_ERROR_MESSAGE
    });
    this.name = 'BayPlayerCredentialStorageError';
    this.operation = operation;
  }
}

const reportStorageFailure = (operation: BayPlayerCredentialStorageOperation): void => {
  // Never attach the JWT or raw storage error. Either may contain sensitive
  // provider/device details; the operation is enough to diagnose the path.
  console.error('[BayPlayerCredentialStorage] JWT storage operation failed', {operation});

  try {
    track('bay_player_jwt_storage_failed', {operation});

  } catch {
    // Observability is best-effort and must never replace the stable storage
    // error contract with an analytics SDK failure.
    console.error('[BayPlayerCredentialStorage] telemetry reporting failed', {operation});
  }
};

export const readBayPlayerJwt = async (): Promise<string | null> => {
  try {
    return await AsyncStorage.getItem(BAY_PLAYER_JWT_STORAGE_KEY);

  } catch {
    reportStorageFailure('read');
    throw new BayPlayerCredentialStorageError('read');
  }
};

export const persistBayPlayerJwt = async (jwt: string): Promise<void> => {
  const operation: Exclude<BayPlayerCredentialStorageOperation, 'read'> = jwt ? 'write' : 'remove';

  try {
    if (operation === 'write') {
      await AsyncStorage.setItem(BAY_PLAYER_JWT_STORAGE_KEY, jwt);

    } else {
      await AsyncStorage.removeItem(BAY_PLAYER_JWT_STORAGE_KEY);
    }

  } catch {
    reportStorageFailure(operation);
    throw new BayPlayerCredentialStorageError(operation);
  }
};
